import type { Media, MediaCategory, Special } from "@/types/media";
import { movies } from "@/mocks/movies";
import { shows } from "@/mocks/shows";
import { variety } from "@/mocks/variety";
import { specials } from "@/mocks/specials";
import { heroSlides } from "@/mocks/heroSlides";

export const allMedia: Media[] = [...movies, ...shows, ...variety] as Media[];

export const allSpecials: Special[] = specials as Special[];

export const heroSlideData = heroSlides;

export interface CategoryMeta {
  key: MediaCategory;
  title: string;
  short: string;
  description: string;
  icon: string;
  accent: "primary" | "accent" | "secondary";
}

export const categoryMeta: Record<MediaCategory, CategoryMeta> = {
  movie: {
    key: "movie",
    title: "电影",
    short: "电影",
    description: "精选院线佳作与经典电影，覆盖科幻、悬疑、剧情等多元类型。",
    icon: "ri-film-line",
    accent: "primary",
  },
  tv: {
    key: "tv",
    title: "电视剧",
    short: "电视剧",
    description: "热播剧集与口碑好剧，古装、都市、悬疑应有尽有。",
    icon: "ri-tv-2-line",
    accent: "accent",
  },
  variety: {
    key: "variety",
    title: "综艺",
    short: "综艺",
    description: "音乐、真人秀、美食综艺，轻松治愈你的每一天。",
    icon: "ri-mic-line",
    accent: "secondary",
  },
};

export const categoryOrder: MediaCategory[] = ["movie", "tv", "variety"];

export const regions: string[] = ["全部", "华语", "欧美", "日韩"];

export const yearFilters: string[] = ["全部", "2025", "2024", "2023", "更早"];

export type SortKey = "latest" | "hot";

export const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新上线" },
  { key: "hot", label: "最热播放" },
];

export function isMediaCategory(value: string): value is MediaCategory {
  return value === "movie" || value === "tv" || value === "variety";
}

export function getMediaById(id: string): Media | undefined {
  return allMedia.find((item) => item.id === id);
}

export function getMediaByCategory(category: MediaCategory): Media[] {
  return allMedia.filter((item) => item.category === category);
}

export function latestMedia(list: Media[], count: number): Media[] {
  return [...list].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, count);
}

export function hotMedia(list: Media[], count: number): Media[] {
  return [...list].sort((a, b) => b.hot - a.hot).slice(0, count);
}

export interface FilterOptions {
  region?: string;
  year?: string;
  sort?: SortKey;
}

export function filterMedia(list: Media[], options: FilterOptions): Media[] {
  const { region = "全部", year = "全部", sort = "latest" } = options;
  let result = list.filter((item) => {
    if (region !== "全部" && item.region !== region) return false;
    if (year === "更早") {
      if (item.year >= 2023) return false;
    } else if (year !== "全部" && String(item.year) !== year) {
      return false;
    }
    return true;
  });
  result =
    sort === "hot"
      ? [...result].sort((a, b) => b.hot - a.hot)
      : [...result].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  return result;
}

export function searchMedia(keyword: string): Media[] {
  const term = keyword.trim().toLowerCase();
  if (!term) return [];
  return allMedia.filter((item) => {
    const haystack = [item.title, item.genre, item.region, item.director, item.summary, ...item.tags]
      .join(" ")
      .toLowerCase();
    return haystack.includes(term);
  });
}

export function getSpecialById(id: string): Special | undefined {
  return allSpecials.find((item) => item.id === id);
}

export function getSpecialMedia(special: Special): Media[] {
  return special.mediaIds
    .map((id) => getMediaById(id))
    .filter((item): item is Media => Boolean(item));
}

export function getRelatedMedia(media: Media, count: number): Media[] {
  const scored = allMedia
    .filter((item) => item.id !== media.id)
    .map((item) => {
      let score = 0;
      if (item.category === media.category) score += 2;
      const sharedTags = item.tags.filter((tag) => media.tags.includes(tag)).length;
      score += sharedTags * 3;
      if (item.genre.split(" / ")[0] === media.genre.split(" / ")[0]) score += 3;
      if (item.region === media.region) score += 1;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || b.item.hot - a.item.hot);
  return scored.slice(0, count).map((entry) => entry.item);
}

export function getRelatedSpecials(currentId: string, count: number): Special[] {
  return allSpecials.filter((item) => item.id !== currentId).slice(0, count);
}

export function formatHot(hot: number): string {
  if (hot >= 10000) return `${(hot / 10000).toFixed(1)}万`;
  return String(hot);
}